import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatMoney, formatQty } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';

interface Lagerwertzeile {
  article_id: string;
  sku: string;
  name: string;
  unit: string;
  gruppe: string | null;
  qty: number;
  einstand_cents: number | null;
  wert_cents: number;
}

interface Lagerwert {
  total_cents: number;
  artikel_bewertet: number;
  artikel_ohne_einstand: number;
  gruppen: { gruppe: string; wert_cents: number }[];
  artikel: Lagerwertzeile[];
}

interface Rohertrag {
  von: string;
  bis: string;
  umsatz_cents: number;
  wareneinsatz_cents: number;
  rohertrag_cents: number;
  quote: number | null;
  belege: number;
  positionen_ohne_kosten: number;
}

function monatsanfang(): string {
  return `${new Date().toISOString().slice(0, 8)}01`;
}
function heute(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ValuationPage() {
  const [wert, setWert] = useState<Lagerwert | null>(null);
  const [ertrag, setErtrag] = useState<Rohertrag | null>(null);
  const [von, setVon] = useState(monatsanfang());
  const [bis, setBis] = useState(heute());
  const [error, setError] = useState('');

  const ladeWert = useCallback(async () => {
    try {
      setWert(await api<Lagerwert>('/api/valuation/lagerwert'));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  const ladeErtrag = useCallback(async () => {
    try {
      setErtrag(await api<Rohertrag>('/api/valuation/rohertrag', { query: { von, bis } }));
    } catch (err) {
      setError(errorText(err));
    }
  }, [von, bis]);

  useEffect(() => {
    void ladeWert();
  }, [ladeWert]);
  useEffect(() => {
    void ladeErtrag();
  }, [ladeErtrag]);

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Card title="Rohertrag (eigene Ware)">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
          <TextField label="von" type="date" value={von} onChange={setVon} />
          <TextField label="bis" type="date" value={bis} onChange={setBis} />
        </div>
        {!ertrag ? (
          <Loading />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              <Kennzahl label="Umsatz (versendet)" wert={formatMoney(ertrag.umsatz_cents)} />
              <Kennzahl label="Wareneinsatz" wert={formatMoney(ertrag.wareneinsatz_cents)} />
              <Kennzahl
                label="Rohertrag"
                wert={formatMoney(ertrag.rohertrag_cents)}
                stark
                farbe={ertrag.rohertrag_cents >= 0 ? 'var(--ok)' : 'var(--bad)'}
              />
              <Kennzahl label="Quote" wert={ertrag.quote != null ? `${ertrag.quote} %` : '—'} />
              <Kennzahl label="Belege" wert={String(ertrag.belege)} />
            </div>
            {ertrag.positionen_ohne_kosten > 0 && (
              <div style={{ marginTop: 12 }}>
                <Badge kind="warn">
                  {ertrag.positionen_ohne_kosten} Position(en) ohne festgeschriebenen Wareneinsatz
                </Badge>
                <span className="small muted" style={{ marginLeft: 8 }}>
                  — vor der Bewertung versendet oder ohne Einstand am Artikel; der Rohertrag ist
                  insoweit zu hoch ausgewiesen.
                </span>
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="Lagerwert (eigene Ware, zu Einstandspreisen)">
        {!wert ? (
          <Loading />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 16 }}>
              <Kennzahl label="Lagerwert gesamt" wert={formatMoney(wert.total_cents)} stark />
              <Kennzahl label="bewertete Artikel" wert={String(wert.artikel_bewertet)} />
              {wert.artikel_ohne_einstand > 0 && (
                <Kennzahl
                  label="ohne Einstand"
                  wert={String(wert.artikel_ohne_einstand)}
                  farbe="var(--warn)"
                />
              )}
            </div>

            {wert.gruppen.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div className="small muted" style={{ fontWeight: 600, marginBottom: 4 }}>
                  nach Warengruppe
                </div>
                {wert.gruppen.map((g) => (
                  <div key={g.gruppe} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', maxWidth: 420 }}>
                    <span>{g.gruppe}</span>
                    <span className="mono">{formatMoney(g.wert_cents)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="Artikel nach Wert" padded={false}>
        {!wert ? (
          <Loading />
        ) : wert.artikel.length === 0 ? (
          <Empty>Kein bewertbarer eigener Bestand.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Gruppe</th>
                  <th className="num">Bestand</th>
                  <th className="num">Einstand</th>
                  <th className="num">Wert</th>
                </tr>
              </thead>
              <tbody>
                {wert.artikel.map((z) => (
                  <tr key={z.article_id}>
                    <td>
                      <span className="mono">{z.sku}</span> {z.name}
                    </td>
                    <td className="small muted">{z.gruppe ?? '—'}</td>
                    <td className="num">{formatQty(z.qty, z.unit)}</td>
                    <td className="num">
                      {z.einstand_cents != null ? (
                        formatMoney(z.einstand_cents)
                      ) : (
                        <Badge kind="warn">fehlt</Badge>
                      )}
                    </td>
                    <td className="num">{formatMoney(z.wert_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function Kennzahl({
  label,
  wert,
  stark,
  farbe,
}: {
  label: string;
  wert: string;
  stark?: boolean;
  farbe?: string;
}) {
  return (
    <div>
      <div className="small muted">{label}</div>
      <strong style={{ fontSize: stark ? '1.4em' : '1.1em', color: farbe }}>{wert}</strong>
    </div>
  );
}
